import { FC, KeyboardEvent, MouseEvent, ReactNode, useEffect } from 'react';
import { Button, LottieAnimation, Week } from 'src/components';
import { defineMessages, useIntl } from 'react-intl';
import { defaultAnimationOptions } from 'src/components/constants';
import { useMediaQuery } from 'react-responsive';
import theme from 'src/utils/theme';
import { useUserContext } from 'src/context/user';
import { StreakApi, TrackingApi } from 'src/api';
import fire from './fire.json';
import * as S from './styles';

const msgs = defineMessages({
  header: { id: 'streaks_celebration_header' },
  description: { id: 'streaks_celebration_info' },
  currentStreak: { id: 'streaks_celebration_current' },
  days: { id: 'streaks_celebration_days_web' },
  cta: { id: 'streaks_celebration_confirm' },
});

interface Props {
  languagePairId?: number | null;
  count?: number;
  isOpen: boolean;
  handleClose: (e?: MouseEvent | KeyboardEvent) => void;
  isFullSize?: boolean;
}

export const StreakCelebration: FC<Props> = ({
  languagePairId,
  count,
  isOpen,
  handleClose,
  isFullSize = false,
}) => {
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const { user } = useUserContext();
  const isPro = !!user?.isPro;

  useEffect(() => {
    if (isOpen) {
      /* istanbul ignore next: analytics */
      void TrackingApi.trackUserAction(
        'screen_viewed',
        'streak_tooltip',
        languagePairId ?? null,
        isPro,
        {
          streak_length: String(count ?? 0),
        },
      );
    }
  }, [count, isOpen, isPro, languagePairId]);

  const onClose = async (e?: MouseEvent | KeyboardEvent) => {
    /* istanbul ignore else: empty else branch */
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    /* istanbul ignore next: analytics */
    void TrackingApi.trackUserAction(
      'clicked_continue_button',
      'streak_tooltip',
      languagePairId ?? null,
      isPro,
      { streak_length: String(count ?? 0) },
    );
    handleClose();
    languagePairId && (await StreakApi.postStreak(languagePairId, { acknowledged: true }));
  };

  return (
    <S.ModalWrapper
      fullSize={!!isFullSize}
      isOpen={isOpen}
      handleClose={e => onClose(e)}
      darkCloseIcon
      hideCloseButton={!!isFullSize}
    >
      <S.Content
        data-testid="stack"
        $spacing={isMobile ? 's4' : 's6'}
        $align="center"
        $justify="center"
        $isFullSize={isFullSize}
      >
        <S.AnimationContainer>
          <LottieAnimation {...defaultAnimationOptions} loop animationData={fire} />
        </S.AnimationContainer>
        <S.Header>{intl.formatMessage(msgs.header)}🔥</S.Header>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
        <S.BodyContainer>
          <S.Subtitle>
            {intl.formatMessage(msgs.currentStreak)}
            <S.Colon>{':'}</S.Colon>
            <S.StreakText>
              {intl.formatMessage(msgs.days, {
                n: count,
                bold: (...chunks: ReactNode[]) => (
                  <S.Number key="Number" data-testid="current-streak-count">
                    {chunks}
                  </S.Number>
                ),
              })}
            </S.StreakText>
          </S.Subtitle>
          <Week count={count ?? 0} />
          <S.TopButtons>
            <Button buttonType="primary" buttonSize="large" onClick={onClose} wide>
              {intl.formatMessage(msgs.cta)}
            </Button>
          </S.TopButtons>
        </S.BodyContainer>
      </S.Content>
    </S.ModalWrapper>
  );
};
